import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { backendApi, clearBackendCache } from '../services/api'

const AuthContext = createContext(null)

const isDev = import.meta.env.DEV

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [appUser, setAppUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // Settes når Supabase sier at brukeren kom fra en reset-password-
  // lenke. App.jsx bruker dette til å tvinge brukeren innom
  // /reset-password før de får resten av appen.
  const [passwordRecovery, setPasswordRecovery] = useState(false)
  // Settes når backend returnerer 403 på /auth/me — typisk fordi
  // brukeren autentiserte hos Supabase (f.eks. Microsoft SSO) men
  // ikke ligger i app_users (invite-only). Vi logger ut fra Supabase
  // i samme slengen så brukeren får en ren tilstand og kan prøve igjen
  // med en annen konto.
  const [unauthorized, setUnauthorized] = useState(false)
  // Brukes for å unngå doble /me-kall når Supabase fyrer
  // INITIAL_SESSION + SIGNED_IN begge ved oppstart med aktiv session.
  const lastFetchedToken = useRef(null)

  useEffect(() => {
    if (isDev) {
      // Dev bypass — auto-login as admin without Supabase
      backendApi.get('/auth/me', { cache: false }).then(({ data }) => {
        setSession({ user: { email: data.email } })
        setAppUser(data)
      }).catch(() => {
        setAppUser(null)
      }).finally(() => setLoading(false))
      return
    }

    // Ikke kall fetchAppUser her — onAuthStateChange fyrer INITIAL_SESSION
    // umiddelbart og håndterer dette, slik at vi unngår doble /me-kall.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { setSession(null); setLoading(false) }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true)
      if (event === 'USER_UPDATED' || event === 'SIGNED_OUT') setPasswordRecovery(false)
      if (event === 'SIGNED_IN') setUnauthorized(false)
      setSession(session)
      // Bare hent brukerdata ved faktisk innlogging eller initial sesjon —
      // ikke ved TOKEN_REFRESHED (token byttes automatisk, brukeren er den samme).
      if (session && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
        fetchAppUser(session.access_token)
      } else if (!session) {
        setAppUser(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchAppUser(token) {
    if (!token || lastFetchedToken.current === token) return
    lastFetchedToken.current = token
    // En ny innlogging kan være en annen bruker: ingenting fra forrige skal
    // gjenbrukes, heller ikke /auth/me.
    clearBackendCache()
    try {
      const { data } = await backendApi.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
        cache: false,
      })
      setAppUser(data)
      setUnauthorized(false)
    } catch (err) {
      setAppUser(null)
      // 403 = autentisert hos Supabase men ikke i app_users. Logg ut
      // fra Supabase så brukeren havner på en ren Login-side med alle
      // pålogging-alternativer (inkl. Microsoft-knappen) tilgjengelige
      // for ny runde. Banneret fra `unauthorized`-flagget overlever
      // signOut fordi det er React-state, ikke session-avhengig.
      if (err?.response?.status === 403) {
        setUnauthorized(true)
        try { await supabase.auth.signOut() } catch { /* ignore */ }
      }
    }
    setLoading(false)
  }

  const signOut = async () => {
    sessionStorage.removeItem('imp_token')
    clearBackendCache()
    lastFetchedToken.current = null
    await supabase.auth.signOut()
    setAppUser(null)
    setSession(null)
    setUnauthorized(false)
  }

  // Modul 3: starte impersonering. Lagrer token i sessionStorage og henter
  // /auth/me på nytt — backend overstyrer da role/customerId.
  async function startImpersonation(customerId, reason) {
    const { data } = await backendApi.post(`/auth/impersonate/${customerId}`,
      { reason },
      { cache: false },
    )
    sessionStorage.setItem('imp_token', data.token)
    clearBackendCache()
    // Re-hent /me for å få oppdatert role/permissions
    const { data: me } = await backendApi.get('/auth/me', { cache: false })
    setAppUser(me)
    return data
  }

  // «Se som bruker»: se appen som en annen konto (kun lesing). Samme
  // token-mekanisme som kunde-impersonering — backend setter rolle/tilganger
  // til målets og markerer modusen som readonly. Returnerer målinfo så kalleren
  // kan navigere (medarbeider → /min-side, ellers /).
  async function seSom(userId) {
    const { data } = await backendApi.post(`/auth/impersonate-user/${userId}`, {}, { cache: false })
    sessionStorage.setItem('imp_token', data.token)
    clearBackendCache()
    const { data: me } = await backendApi.get('/auth/me', { cache: false })
    setAppUser(me)
    return data
  }

  async function endImpersonation() {
    try {
      await backendApi.post('/auth/impersonate/end', {}, { cache: false })
    } catch { /* ignore */ }
    sessionStorage.removeItem('imp_token')
    // Også når avslutningen feilet: identiteten byttes uansett her.
    clearBackendCache()
    const { data: me } = await backendApi.get('/auth/me', { cache: false })
    setAppUser(me)
  }

  // Permission-lookup. Brukes overalt (route-guards, sidebar, knapper).
  // Administrator har implisitt alle tilganger — vi stoler på at
  // backend også seedet alle permissions til admin-rollen, men denne
  // dobbelbeskyttelsen gjør at admin aldri ved et uhell blir låst ute
  // om katalogen er usynket.
  const permissions = useMemo(() => new Set(appUser?.permissions || []), [appUser?.permissions])
  const hasPermission = (key) => {
    if (!appUser) return false
    if (appUser.role === 'administrator') return true
    if (!key) return true
    return permissions.has(key)
  }
  // hasAnyPermission(['a.b', 'a.c']) → true hvis én av dem finnes.
  // Nyttig når én seksjon-menypunkt åpner en side som inneholder flere
  // under-permissions.
  const hasAnyPermission = (keys) => {
    if (!appUser) return false
    if (appUser.role === 'administrator') return true
    if (!keys || keys.length === 0) return true
    return keys.some(k => permissions.has(k))
  }

  return (
    <AuthContext.Provider value={{
      user: session?.user || null,
      role: appUser?.role || null,
      appUser,
      loading,
      signOut,
      isAdmin: appUser?.role === 'administrator',
      isLeder: appUser?.role === 'leder',
      hasPermission,
      hasAnyPermission,
      permissions: appUser?.permissions || [],
      passwordRecovery,
      unauthorized,
      // Impersonation: settes når backend rapporterer at vi er en admin som
      // ser som kunde. Sidebar/Header bruker dette til å vise rødt banner.
      impersonating: appUser?.impersonating || null,
      startImpersonation,
      seSom,
      endImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
